/* eslint-disable max-lines */
import React, { ReactNode } from 'react';
import EditIcon03 from '@untitled-ui/icons-react/build/esm/Edit03';

import { Tooltip } from 'antd';
import {
  BaseButton,
  CheckboxFilter,
  ColumnMenu,
  DateRange,
  DateRangeFilter,
  DownloadPdfButton,
  Icon,
  LinkCopyButton,
  RefreshKycStatusButton,
  RenewVerificationButton,
  StatusTag,
  TableSort,
  TableSortValues,
} from 'components';
import RedirectButton from 'components/Buttons/RedirectButton';
import { format } from 'date-fns';
import { GetEntityModel, GetPersonWithRolesModel } from 'solstice-types';
import { getCookieValue } from './helpers';
import { PACKAGE_CHECK_ICON } from './icons';
import { Status } from './interfaces';
import { getFullName, getInitialsFromCookies } from './utils';

export const UNASSIGNED_LABEL = 'Unassigned';

export type GroupSortKey =
  | 'registeredName'
  | 'entityTypeName'
  | 'abn'
  | 'kycStatus'
  | 'assignedToUser'
  | 'statusUpdatedAt';

export type GroupSort = TableSort<GroupSortKey>;

// Comparable value per sortable column, kept beside the columns so a column's sort
// value and the value it renders stay in step.
export const groupSortValues: TableSortValues<GroupSortKey, GetEntityModel> = {
  registeredName: (record) => record.registeredName?.toLowerCase() ?? '',
  entityTypeName: (record) => record.entityTypeName?.toLowerCase() ?? '',
  abn: (record) => record.abn ?? '',
  kycStatus: (record) => record.kycStatus?.toLowerCase() ?? '',
  assignedToUser: (record) => record.assignedToUser?.userName?.toLowerCase() ?? '',
  statusUpdatedAt: (record) => new Date(record.statusUpdatedAt ?? '').getTime() || 0,
};

// Values the checkbox filters match on. These are the labels shown in the table, so a
// tick in the panel reads the same as the cell it selects.
export const groupFilterValues = {
  entityTypeName: (record: GetEntityModel) => record.entityTypeName || '-',
  assignedToUser: (record: GetEntityModel) => record.assignedToUser?.userName || UNASSIGNED_LABEL,
};

export interface GroupFilters {
  statusUpdatedAt: DateRange;
  entityTypeName: string[];
  assignedToUser: string[];
}

export const emptyGroupFilters: GroupFilters = {
  statusUpdatedAt: null,
  entityTypeName: [],
  assignedToUser: [],
};

const isGroupColumnFiltered = (filters: GroupFilters | undefined, columnKey: GroupSortKey) => {
  if (columnKey === 'statusUpdatedAt') return !!filters?.statusUpdatedAt;
  if (columnKey === 'entityTypeName' || columnKey === 'assignedToUser') {
    return (filters?.[columnKey]?.length ?? 0) > 0;
  }

  return false;
};

interface GroupColumnsOptions {
  sort?: GroupSort;
  onSortChange?: (sort: GroupSort) => void;
  filters?: GroupFilters;
  onFiltersChange?: (filters: GroupFilters) => void;
  /** Options for the checkbox filters, derived from the records currently loaded. */
  filterOptions?: { entityTypeName: string[]; assignedToUser: string[] };
}

export const getGroupColumns = ({
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  filterOptions,
}: GroupColumnsOptions = {}) => {
  const menu = (columnKey: GroupSortKey, title: string, filterPanel?: ReactNode) => (
    <ColumnMenu
      title={title}
      activeSort={sort?.columnKey === columnKey ? sort.direction : undefined}
      onSortAscending={() => onSortChange?.({ columnKey, direction: 'ascending' })}
      onSortDescending={() => onSortChange?.({ columnKey, direction: 'descending' })}
      filterPanel={filterPanel}
      isFiltered={isGroupColumnFiltered(filters, columnKey)}
    />
  );

  const checkboxFilter = (columnKey: 'entityTypeName' | 'assignedToUser') => (
    <CheckboxFilter
      options={filterOptions?.[columnKey] ?? []}
      value={filters?.[columnKey] ?? []}
      onChange={(selected) =>
        onFiltersChange?.({ ...emptyGroupFilters, ...filters, [columnKey]: selected })
      }
    />
  );

  return [
    {
      title: menu('registeredName', 'Group Name'),
      dataIndex: 'registeredName',
      key: 'registeredName',
      width: '17%',
      render: (text: string, record: GetEntityModel) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800 overflow-hidden text-ellipsis">
            <Tooltip title={record.registeredName} placement="top">
              {record.registeredName}
            </Tooltip>
          </span>
          <span className="text-xs text-gray-500 font-medium">
            {record.entityPersonCount ? `${record.entityPersonCount} individuals` : ''}
          </span>
        </div>
      ),
    },
    {
      title: menu('entityTypeName', 'Type', checkboxFilter('entityTypeName')),
      dataIndex: 'entityTypeName',
      key: 'entityTypeName',
      width: '17%',
    },
    {
      title: menu('abn', 'ABN'),
      dataIndex: 'abn',
      key: 'abn',
      render: (record: string) => record ?? '-',
      width: '16%',
    },
    {
      title: menu('kycStatus', 'Status'),
      dataIndex: 'kycStatus',
      key: 'kycStatus',
      render: (record: Status) => <StatusTag status={record} />,
      width: '17%',
    },
    {
      title: menu('assignedToUser', 'Assigned To', checkboxFilter('assignedToUser')),
      dataIndex: 'assignedToUser',
      key: 'assignedToUser',
      width: '17%',
      render: (data: any) => {
        return data?.userName ?? '-';
      },
    },
    {
      title: menu(
        'statusUpdatedAt',
        'Last Updated',
        <DateRangeFilter
          value={filters?.statusUpdatedAt ?? null}
          onChange={(statusUpdatedAt) =>
            onFiltersChange?.({ ...emptyGroupFilters, ...filters, statusUpdatedAt })
          }
        />
      ),
      dataIndex: 'statusUpdatedAt',
      key: 'statusUpdatedAt',
      width: '17%',
      render: (record: string) => (
        <div className="flex flex-col">
          <span className="text-gray-800">{format(new Date(record), 'dd/MM/yyyy - hh:mm a')}</span>
          {/* <span className="text-gray-500 text-xs">by N/A</span> */}
        </div>
      ),
    },
  ];
};

export type IndividualSortKey =
  | 'firstName'
  | 'type'
  | 'status'
  | 'assignedToUser'
  | 'statusUpdatedAt';

export type IndividualSort = TableSort<IndividualSortKey>;

// The individuals table is fed GetPersonModel from the search endpoint but renders
// fields from GetPersonWithRolesModel, and the two models disagree on `address`. This
// covers just the fields the sort reads, so both models satisfy it.
export interface IndividualSortRecord {
  firstName?: string | null;
  lastName?: string | null;
  status?: string | null;
  statusUpdatedAt?: string;
  entityRoles?: unknown[] | null;
  assignedToUser?: { userName?: string | null };
}

const getIndividualTypeLabel = (record: IndividualSortRecord) =>
  record.entityRoles && record.entityRoles.length > 0
    ? 'Multiple Individuals'
    : 'Single Individual';

// Comparable value per sortable column, kept beside the columns so a column's sort
// value and the value it renders stay in step.
export const individualSortValues: TableSortValues<IndividualSortKey, IndividualSortRecord> = {
  firstName: (record) => `${record.firstName ?? ''} ${record.lastName ?? ''}`.trim().toLowerCase(),
  type: (record) => getIndividualTypeLabel(record),
  status: (record) => record.status?.toLowerCase() ?? '',
  assignedToUser: (record) => record.assignedToUser?.userName?.toLowerCase() ?? '',
  statusUpdatedAt: (record) => new Date(record.statusUpdatedAt ?? '').getTime() || 0,
};

export const emptyIndividualFilters: IndividualFilters = {
  statusUpdatedAt: null,
  type: [],
  assignedToUser: [],
};

const isColumnFiltered = (filters: IndividualFilters | undefined, columnKey: IndividualSortKey) => {
  if (columnKey === 'statusUpdatedAt') return !!filters?.statusUpdatedAt;
  if (columnKey === 'type' || columnKey === 'assignedToUser') {
    return (filters?.[columnKey]?.length ?? 0) > 0;
  }

  return false;
};

// Values the checkbox filters match on. These are the labels shown in the table, so a
// tick in the panel reads the same as the cell it selects.
export const individualFilterValues = {
  type: (record: IndividualSortRecord) => getIndividualTypeLabel(record),
  assignedToUser: (record: IndividualSortRecord) =>
    record.assignedToUser?.userName || UNASSIGNED_LABEL,
};

export interface IndividualFilters {
  statusUpdatedAt: DateRange;
  type: string[];
  assignedToUser: string[];
}

interface IndividualColumnsOptions {
  sort?: IndividualSort;
  onSortChange?: (sort: IndividualSort) => void;
  filters?: IndividualFilters;
  onFiltersChange?: (filters: IndividualFilters) => void;
  /** Options for the checkbox filters, derived from the records currently loaded. */
  filterOptions?: { type: string[]; assignedToUser: string[] };
}

export const getIndividualColumns = ({
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  filterOptions,
}: IndividualColumnsOptions = {}) => {
  const menu = (columnKey: IndividualSortKey, title: string, filterPanel?: ReactNode) => (
    <ColumnMenu
      title={title}
      activeSort={sort?.columnKey === columnKey ? sort.direction : undefined}
      onSortAscending={() => onSortChange?.({ columnKey, direction: 'ascending' })}
      onSortDescending={() => onSortChange?.({ columnKey, direction: 'descending' })}
      filterPanel={filterPanel}
      isFiltered={isColumnFiltered(filters, columnKey)}
    />
  );

  const checkboxFilter = (columnKey: 'type' | 'assignedToUser') => (
    <CheckboxFilter
      options={filterOptions?.[columnKey] ?? []}
      value={filters?.[columnKey] ?? []}
      onChange={(selected) =>
        onFiltersChange?.({ ...emptyIndividualFilters, ...filters, [columnKey]: selected })
      }
    />
  );

  return [
    {
      title: menu('firstName', 'Name'),
      dataIndex: 'firstName',
      key: 'firstName',
      width: '20%',
      render: (text: string, record: GetPersonWithRolesModel) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800">
            {record.firstName} {record.lastName}
          </span>
          <span className="text-xs text-gray-500 font-medium">
            {record.entityRoles?.length && record.entityRoles[0].roleName !== 'Creator'
              ? `${record.entityRoles[0]?.entityName}`
              : ''}
          </span>
        </div>
      ),
    },

    {
      title: menu('type', 'Type', checkboxFilter('type')),
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (text: string, record: GetPersonWithRolesModel) => getIndividualTypeLabel(record),
    },
    {
      title: menu('status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      render: (status: string) => <StatusTag status={status ?? null} />,
    },
    {
      title: menu('assignedToUser', 'Assigned To', checkboxFilter('assignedToUser')),
      dataIndex: 'assignedToUser',
      key: 'assignedToUser',
      width: '20%',
      render: (record: any) => {
        if (!record) return '-';

        return record?.userName ?? '-';
      },
    },
    {
      title: (
        <ColumnMenu
          title="Last Updated"
          activeSort={sort?.columnKey === 'statusUpdatedAt' ? sort.direction : undefined}
          onSortAscending={() =>
            onSortChange?.({ columnKey: 'statusUpdatedAt', direction: 'ascending' })
          }
          onSortDescending={() =>
            onSortChange?.({ columnKey: 'statusUpdatedAt', direction: 'descending' })
          }
          isFiltered={!!filters?.statusUpdatedAt}
          filterPanel={
            <DateRangeFilter
              value={filters?.statusUpdatedAt ?? null}
              onChange={(statusUpdatedAt) =>
                onFiltersChange?.({ ...emptyIndividualFilters, ...filters, statusUpdatedAt })
              }
            />
          }
        />
      ),
      dataIndex: 'statusUpdatedAt',
      key: 'statusUpdatedAt',
      width: '20%',
      render: (record: string) => (
        <div className="flex flex-col">
          <span className="text-gray-800">{format(new Date(record), 'dd/MM/yyyy - hh:mm a')}</span>
        </div>
      ),
    },
  ];
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : format(d, 'dd/MM/yyyy - hh:mm a');
};

export const entityIndividualsColumns = (showModal: (record: any) => void) => [
  {
    title: 'Individual',
    key: 'name',
    width: '18%',
    render: (record: any) => {
      const dateOfBirth = record?.dateOfBirth ? new Date(record.dateOfBirth) : null;
      const hasDateOfBirth = dateOfBirth && !isNaN(dateOfBirth.getTime());

      return (
        <div className="flex flex-col overflow-hidden">
          <span className="whitespace-nowrap text-ellipsis overflow-hidden text-gray-900 font-medium">
            {getFullName(record?.firstName, record?.lastName) || '-'}
          </span>
          <span className="text-xs text-gray-500 font-normal">
            {hasDateOfBirth ? format(dateOfBirth, 'dd/MM/yyyy') : 'No date of birth'}
          </span>
        </div>
      );
    },
  },
  {
    title: 'Address',
    key: 'address',
    width: '18%',
    // The generated type says Address, but the endpoint returns a preformatted string.
    render: (record: any) => {
      const address =
        typeof record?.address === 'string' ? record.address : record?.address?.formattedAddress;

      if (!address) return '-';

      return (
        <Tooltip title={address} placement="top">
          <div className="overflow-hidden whitespace-nowrap text-ellipsis">{address}</div>
        </Tooltip>
      );
    },
  },
  {
    title: 'Verification Status',
    dataIndex: 'status',
    key: 'status',
    width: '13%',
    render: (text: string) => <StatusTag status={text} />,
  },
  {
    title: 'Last Updated',
    key: 'statusUpdatedAt',
    width: '16%',
    render: (record: any) => (
      <div className="flex flex-col">
        <span className="text-gray-900">{formatDateTime(record?.statusUpdatedAt)}</span>
        <span className="text-xs text-gray-500 font-normal">
          Started {formatDateTime(record?.lastKycFlowCreatedAt)}
        </span>
      </div>
    ),
  },
  {
    title: 'Role',
    dataIndex: 'roleName',
    key: 'roleName',
    width: '12%',
    render: (text: string) => (
      <div className="overflow-hidden whitespace-nowrap text-ellipsis">{text || '-'}</div>
    ),
  },
  {
    title: 'Ownership',
    key: 'ownershipPercentage',
    width: '13%',
    render: (record: any) => {
      const percentage = record?.ownershipPercentage;
      const held = record?.beneficiallyHeld;

      return (
        <div className="flex flex-col">
          <span className="text-gray-900">
            {percentage == null ? '-' : `${parseFloat(String(percentage))}%`}
          </span>
          {held != null && (
            <span className="text-xs text-gray-500 font-normal">
              {held ? 'Beneficially Held' : 'Not Beneficially Held'}
            </span>
          )}
        </div>
      );
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (record: any) => (
      <div className="flex gap-2.5 text-gray-700">
        <Tooltip title="Edit Verification Status" placement="top">
          <BaseButton
            className="min-h-8 min-w-8 p-0 secondary-button"
            onClick={() => showModal(record)}
          >
            <EditIcon03 className="h-4 w-4" />
          </BaseButton>
        </Tooltip>

        <RedirectButton record={record} />

        <LinkCopyButton record={record} />

        <DownloadPdfButton record={record} />
      </div>
    ),
  },
];

export const individualDetailsColumns = (showModal: (record: any) => void) => [
  {
    title: 'Started On',
    dataIndex: 'statusUpdatedAt',
    key: 'statusUpdatedAt',
    render: (text: string) => {
      if (!text) return '—';
      const d = new Date(text);
      return isNaN(d.getTime()) ? '—' : format(d, 'dd/MM/yyyy - hh:mm a');
    }
  },
  {
    title: 'Assigned To',
    dataIndex: 'assignedToUser',
    key: 'assignedToUser',
    render: (data: any) => {
      return data?.userName ?? '-';
    },
  },
  {
    title: 'Associated Entity',
    dataIndex: 'associatedEntity',
    key: 'associatedEntity',
    render: (text: any) => (text ? text.map((item: any) => item.entityName) : 'None'),
  },

  {
    title: 'Verification Status',
    dataIndex: 'status',
    key: 'status',
    width: '20%',
    render: (status: string) => {
      return <StatusTag status={status} />;
    },
  },

  {
    title: 'Last Status Update',
    dataIndex: 'statusUpdatedAt',
    key: 'statusUpdatedAt',
    width: '20%',
    render: (text: string) => {
      if (!text) return '—';
      const d = new Date(text);
      return isNaN(d.getTime()) ? '—' : format(d, 'dd/MM/yyyy - hh:mm a');
    },
  },
  {
    title: 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    render: (text: string, record: any) => {
      return (
        <div className="flex gap-2.5 text-gray-700">
          <RefreshKycStatusButton record={record}></RefreshKycStatusButton>

          <Tooltip title="Edit Verification Status" placement="top">
            <BaseButton
              className="secondary-button min-h-8 min-w-8 p-0"
              onClick={() => showModal(record)}
            >
              <EditIcon03 className="h-4 w-4" />
            </BaseButton>
          </Tooltip>

          <RedirectButton record={record} />

          <LinkCopyButton record={record} />

          <DownloadPdfButton record={record} />

          <RenewVerificationButton record={record} />
        </div>
      );
    },
  },
];

export const companyDrawerIndividualColumns = [
  {
    title: 'Individual Name',
    dataIndex: 'name',
    key: 'name',
    width: '20%',
    render: (record: any) => (
      <div className="overflow-hidden whitespace-nowrap text-ellipsis text-gray-900 font-medium">
        {getFullName(record?.firstName, record?.lastName) ?? ''}
      </div>
    ),
  },
  {
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
    width: '20%',

    render: (address: any) => {
      if (!address) return '-';
      return (
        <div className="break-words min-w-40 max-w-full">
          {address.street || ''}, {address.city || ''}, {address.state || ''},{' '}
          {address.postcode || ''}
        </div>
      );
    },
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
    render: (role: string) => {
      return role ?? '-';
    },
  },
  {
    title: 'Beneficially Held',
    dataIndex: 'beneficiallyHeld',
    key: 'beneficiallyHeld',
    render: (text: any) => (text ? 'Yes' : 'No'),
  },
  {
    title: 'Total Ownership',
    dataIndex: 'ownership',
    key: 'ownership',
    render: (text: any) => {
      if (!text) return '-';
      const formattedText = parseFloat(text) === 100 ? '100%' : `${parseFloat(text)}%`;
      return formattedText;
    },
  },
];

export const auditLogColumns = [
  {
    title: 'Date',
    dataIndex: 'timestamp',
    key: 'timestamp',
    width: '18%',
    render: (data: string) => {
      const formattedDate = data ? format(new Date(Date.parse(data)), 'dd/MM/yyyy - hh:mm a') : '-';

      return <div className="text-gray-800 font-normal">{formattedDate}</div>;
    },
  },

  {
    title: 'Action',
    dataIndex: 'detail',
    key: 'detail',
    render: (data: string) => {
      const [actionText, noteText] = data.split('\n\n');

      const renderWithTags = (text: string) => {
        const result: React.ReactNode[] = [];
        const words = text.split(' ');

        words.forEach((word, index) => {
          const cleanedWord = word.replace(/[.,!?]/g, '');

          const match = actionTypeStatus.find((option) => option.label === cleanedWord);
          if (match) {
            result.push(<StatusTag status={match.value} key={`${word}-${index}`} />);
          } else {
            result.push(<span key={`${word}-${index}`}>{word} </span>);
          }
        });

        return result;
      };

      const renderNoteText = (text: string) => {
        if (!text) return null;

        const words = text.split(' ');
        return (
          <div className="text-gray-900 bg-gray-50 p-1 text-sm mt-2 w-fit">
            <strong>{words[0]}</strong> {words.slice(1).join(' ')}
          </div>
        );
      };

      return (
        <div className="text-gray-800 font-normal">
          <div>{renderWithTags(actionText || '')}</div>

          {noteText && renderNoteText(noteText)}
        </div>
      );
    },
  },

  {
    title: 'KYC ID',
    dataIndex: 'verificationId',
    key: 'verificationId',
    width: '18%',
    render: (data: string) => {
      if (!data) return '-';

      return <div className="text-gray-800 font-normal">{data}</div>;
    },
  },
  {
    title: 'Done by',
    dataIndex: 'eventSource',
    key: 'eventSource',
    width: '18%',
    render: (data: string) => {
      if (!data) return '-';

      const displayText = data === 'Provider' ? 'System' : data;

      return (
        <div className="text-gray-800 font-normal flex items-center gap-1.5">
          <div className="flex items-center justify-center w-[30px] h-[30px] text-primary-600 bg-gray-100 font-semibold rounded-full">
            {data === 'Provider' ? (
              <Icon src={PACKAGE_CHECK_ICON} alt="divider" />
            ) : (
              getInitialsFromCookies(data)
            )}
          </div>
          {displayText}
        </div>
      );
    },
  },
];

export const billingColumns = [
  {
    title: 'Date',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: '18%',
    render: (data: string) => {
      if (!data) return '-';

      return (
        <div className="text-gray-800 font-normal">{format(data, "dd'/'MM/yyyy - hh:mm a")}</div>
      );
    },
  },
  {
    title: 'Name',
    dataIndex: 'entityName',
    key: 'entityName',
    width: '20%',
    render: (data: string) => {
      return <div className="text-gray-800 font-normal">{data}</div>;
    },
  },
  {
    title: 'Entity Type',
    dataIndex: 'entityType',
    key: 'entityType',
    render: (data: string) => {
      return <div className="text-gray-800 font-normal">{data}</div>;
    },
  },
  {
    title: 'UBO',
    dataIndex: 'uboFlowCount',
    key: 'uboFlowCount',
    render: (data: string = '0') => {
      return <div className="text-gray-800 font-normal">{data}</div>;
    },
  },
  {
    title: 'KYC',
    dataIndex: 'kycFlowCount',
    key: 'kycFlowCount',
    render: (data: string = '0') => {
      return <div className="text-gray-800 font-normal">{data}</div>;
    },
  },
  {
    title: 'Client',
    dataIndex: 'client',
    hidden: getCookieValue('roleId') === 4, //User is guest
    key: 'client',
    render: (data: string = '-') => {
      return <div className="text-gray-800 font-normal">{data}</div>;
    },
  },
  //TODO Uncomment when AML and DVS are implemented
  // {
  //   title: 'AML',
  //   dataIndex: 'aml',
  //   key: 'aml',
  //   render: (data: string = '0') => {
  //     return <div className="text-gray-800 font-normal">{data}</div>;
  //   },
  // },
  // {
  //   title: 'DVS',
  //   dataIndex: 'dvs',
  //   key: 'dvs',
  //   render: (data: string = '0') => {
  //     return <div className="text-gray-800 font-normal">{data}</div>;
  //   },
  // },
];

export const actionTypeStatus = [
  { label: 'All', value: 'all' },
  { label: 'Created', value: 'Provider' },
  { label: 'Expired', value: 'Expired' },
  { label: 'Verified', value: 'Verified' },
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Error', value: 'ReviewKyc' },
  { label: 'Initial', value: 'NotVerified' },
  { label: 'ManuallyApproved', value: 'ManuallyApproved' },
  { label: 'ManuallyRejected', value: 'ManuallyRejected' },
  { label: 'InProgress', value: 'InProgress' },
];
